import { useCallback, useEffect, useState } from 'react';

import { PeerParticipant, PeerTeam, PeerTypes, useListCampaignPeers } from '@givebacks/api/store';
import { SearchableComboBoxItem, SearchableSelect, useCreateSearchFn } from 'core/Components';

import { useDonorTranslations } from '../../../../../hooks';
import { UsePeerCreditForm } from '../usePeerCredits';
import { PeerMultiSelect } from './PeerMultiSelect';
import { CreditValue } from './PeerSelect.types';

// Custom hook to create useFetchSource for SearchableSelect
const usePeerSearchFn = (type: PeerTypes, storeId: string, storeItemId: string) => {
  const useSearchPeersFromSelect = (searches: Record<string, { value: string }>) => {
    return useListCampaignPeers({
      campaignId: storeItemId,
      causeId: storeId,
      peer_type: type,
      queryOptions: {
        searches: {
          name: { value: searches.name?.value || '' },
        },
        order: {
          field: 'first_name',
          direction: 'asc',
        },
      },
    });
  };

  return useCreateSearchFn({
    useSearchFn: useSearchPeersFromSelect,
    select: (data: { participants?: PeerParticipant[]; teams?: PeerTeam[] } | undefined) => {
      if (!data || !data[type]) return [];

      return (
        data[type]?.map((peer: PeerParticipant | PeerTeam) => {
          if (type === 'participants') {
            const participant = peer as PeerParticipant;
            const fullName = `${participant.first_name} ${participant.last_name}`.trim();
            const displayName = participant.team?.name
              ? `${fullName} - ${participant.team.name}`
              : fullName;
            return {
              value: participant.uuid,
              label: displayName,
              type: 'credit_to',
            };
          } else {
            const team = peer as PeerTeam;
            return {
              value: team.uuid,
              label: team.name,
              type: 'credit_to',
            };
          }
        }) || []
      );
    },
    searchFields: ['name', 'last_name', 'first_name'],
    params: { type, storeId, storeItemId },
  });
};

const PeerSelect = ({
  type,
  form,
  storeId,
  storeItemId,
  isMultiSelect,
}: {
  type: PeerTypes;
  form: UsePeerCreditForm;
  storeItemId: string;
  storeId: string;
  isMultiSelect: boolean;
}) => {
  const { t } = useDonorTranslations({
    storeItemId,
    storeId,
  });
  const [value, setValue] = useState<CreditValue>();

  const handleSelect = useCallback(
    (value?: string | null, selectedOption?: SearchableComboBoxItem) => {
      if (!value || !selectedOption) {
        return setValue(undefined);
      }
      const label =
        selectedOption.label === 'created-option'
          ? selectedOption.label + ' - Write-in'
          : selectedOption.label;
      const type = selectedOption.type === 'created-option' ? 'credit_write_in' : 'credit_to';
      setValue({ value, label, type });
    },
    []
  );

  // Create the useFetchSource function for SearchableSelect
  const useFetchSource = usePeerSearchFn(type, storeId, storeItemId);

  useEffect(() => {
    if (value) {
      form.setFieldValue('credits', [value]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return isMultiSelect ? (
    <PeerMultiSelect type={type} form={form} storeId={storeId} storeItemId={storeItemId} />
  ) : (
    <SearchableSelect.Combobox
      useFetchSource={useFetchSource}
      placeholder={`Search for a ${
        type === 'participants' ? `${t('participant').toLowerCase()}` : `${t('team').toLowerCase()}`
      }`}
      value={value?.value || null}
      onChange={handleSelect}
      createOption={({ searchValue }) =>
        type === 'participants' && searchValue.length >= 2 ? `+ Write in - ${searchValue}` : null
      }
      nothingFoundMessage="Nothing Found"
    />
  );
};

export { PeerSelect };
